import AddIcon from '@mui/icons-material/Add';
import { Button, styled, Typography, useTheme } from '@mui/material';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { useState } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { PageContainer } from '../../../components/Layout/PageContainer';
import { SearchInput } from '../../../CoreComponents/base/SearchInput';
import { FileIngestionConfig } from '../../../services/queries/FileIngestionQueries';
import { FieldAutomationConfigForm } from './FieldAutomationConfigForm';
import { FormErrorBoundary } from './components/FormErrorBoundary';
import { useFieldAutomationColDefs } from './hooks/useFieldAutomationColDefs';
import { useFieldAutomationConfigs } from './state/field-automation-state';

const HeaderContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const HeaderSlotRight = styled('div')`
  display: flex;
  height: 40px;
  justify-content: center;
  gap: 0.5rem;
`;

const Container = styled('div')`
  display: flex;
  width: 100%;
  gap: 0.5rem;
  justify-content: space-between;
  align-items: center;
`;

const GridContainer = styled('div')`
  width: 100%;
  flex: 1;
  margin-top: 1rem;
  & .ag-cell:has(.field-automation-menu) {
    padding: 0;
  }
`;

const SearchContainer = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  height: 40px;
  gap: 1rem;
  margin-top: 1rem;
`;

export function FieldAutomationConfig() {
  const { colors } = useTheme();
  const { data: configs } = useFieldAutomationConfigs();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedConfig, setSelectedConfig] = useState<FileIngestionConfig | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const { columnDefs, defaultColDef } = useFieldAutomationColDefs({
    onEdit: (config: FileIngestionConfig) => {
      setSelectedConfig(config);
      setIsFormOpen(true);
    },
  });

  const onGridReady = (event: GridReadyEvent) => {
    setGridApi(event.api);
  };

  const onSearchTermChange = (value: string) => {
    if (!gridApi) {
      return;
    }
    gridApi.setGridOption('quickFilterText', value);
    setSearchTerm(value);
  };

  const handleCreateClick = () => {
    setSelectedConfig(null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedConfig(null);
  };

  return (
    <PageContainer>
      {isFormOpen && (
        <FormErrorBoundary onClose={handleCloseForm}>
          <FieldAutomationConfigForm config={selectedConfig} onClose={handleCloseForm} />
        </FormErrorBoundary>
      )}

      <HeaderContainer>
        <Container>
          <Typography variant='h4' color={colors.primary[90]}>
            Field Automation
          </Typography>
          <HeaderSlotRight>
            <Button
              color='secondary'
              variant='contained'
              size='medium'
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
            >
              Create Configuration
            </Button>
          </HeaderSlotRight>
        </Container>
        <SearchContainer>
          <SearchInput
            style={{ width: '200px' }}
            value={searchTerm}
            onChange={onSearchTermChange}
            placeholder='Search configurations'
          />
        </SearchContainer>
      </HeaderContainer>

      <GridContainer>
        <AgTable
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          rowData={configs || []}
          suppressMovableColumns={true}
          suppressRowDrag={true}
        />
      </GridContainer>
    </PageContainer>
  );
}
